// Team data loader with caching and lazy loading
const sportCache = new Map();
const sportMap = {
  baseball: ['mlb'],
  football: ['nfl', 'ncaa', 'cfl'],
  basketball: ['ncaa', 'wnba', 'nba'],
  hockey: ['nhl'],
  golf: ['golf'],
  tennis: ['tennis'],
  fighting: ['ufc', 'boxing'],
  soccer: ['soccer'],
};

/**
 * Loads team data for a specific sport with caching
 * @param {string} sport - The sport name (nfl, nba, nhl, mlb, wnba, ncaa)
 * @returns {Promise<Object>} Team data object
 */
export const getTeamsForSport = async (sport) => {
  try {
    const { keyName, leagues } = getLeagues(sport);
    if (!leagues) throw 'sport not found';
    // Return cached data if available
    if (sportCache.has(keyName)) {
      return sportCache.get(keyName);
    }

    // Dynamically import the sport-specific team data
    const teamData = Object.assign(
      {},
      ...(await Promise.all(
        leagues.map(async (league) => {
          const module = await import(
            `../consts/teamsDisplayNames/${league}.js`
          );
          return module.default;
        }),
      )),
    );

    // Cache the loaded data
    sportCache.set(keyName, teamData);

    return teamData;
  } catch (error) {
    console.warn(`Failed to load team data for sport: ${sport}`, error);
    return {};
  }
};

/**
 * Preloads team data for multiple sports
 * @param {string[]} sports - Array of sport names to preload
 * @returns {Promise<Object[]>} Array of team data objects
 */
export const preloadTeams = async (sports) => {
  const promises = sports.map((sport) => getTeamsForSport(sport));
  return Promise.all(promises);
};

/**
 * Clears the team data cache
 */
export const clearTeamCache = () => {
  sportCache.clear();
};

/**
 * Gets cached team data without loading
 * @param {string} sport - The sport name
 * @returns {Object|null} Cached team data or null if not cached
 */
export const getCachedTeams = (sport) => {
  const { keyName, leagues } = getLeagues(sport);
  if (!leagues) return null;
  return sportCache.get(keyName) || null;
};

/**
 * Checks if team data is cached for a sport
 * @param {string} sport - The sport name
 * @returns {boolean} True if cached, false otherwise
 */
export const isTeamDataCached = (sport) => {
  const { keyName, leagues } = getLeagues(sport);
  if (!leagues) return false;
  return sportCache.has(keyName);
};

export const getLeagues = (sport) => {
  const leagues = sportMap[sport] || null;
  if (!leagues) return { keyName: null, leagues: null };
  const keyName = leagues.join('-');
  return { keyName, leagues };
};
